// =============================================================================
// Form de alta/edición de producto. Crear un producto "vendible" implica varios
// pasos que el servidor encadena: producto → presentación base (factor 1) →
// precio en la lista default → (opcional) código de barras. Para edición solo
// actualiza el producto. Las unidades existen desde el alta del tenant.
// =============================================================================
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabase";
import { catalog, ApiError } from "../lib/api";

interface Unit { id: string; code: string; name: string }
interface PriceList { id: string; name: string }

export interface ProductFormInit {
  id?: string; sku?: string; name?: string; baseUnitId?: string; itbmsRate?: string;
}

export function ProductForm({ init, onDone, onCancel }: {
  init?: ProductFormInit; onDone: () => void; onCancel: () => void;
}) {
  const qc = useQueryClient();
  const editing = !!init?.id;
  const [f, setF] = useState({
    sku: init?.sku ?? "", name: init?.name ?? "",
    baseUnitId: init?.baseUnitId ?? "", itbmsRate: init?.itbmsRate ?? "rate_7",
    price: "", barcode: "",
  });
  const [msg, setMsg] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });

  // En edición, catalog_list no trae base_unit_id; lo cargamos para preseleccionar.
  useEffect(() => {
    if (!editing || f.baseUnitId) return;
    supabase.from("products").select("base_unit_id").eq("id", init!.id!).maybeSingle()
      .then(({ data }) => { if (data) setF((prev) => ({ ...prev, baseUnitId: (data as { base_unit_id: string }).base_unit_id })); });
  }, [editing, init, f.baseUnitId]);

  const units = useQuery({
    queryKey: ["units"],
    queryFn: async () => {
      const { data, error } = await supabase.from("units_of_measure").select("id, code, name").order("name");
      if (error) throw error;
      return (data ?? []) as Unit[];
    },
  });
  const defaultList = useQuery({
    queryKey: ["price_list_default"],
    queryFn: async () => {
      const { data } = await supabase.from("price_lists").select("id, name").eq("is_default", true).maybeSingle();
      return data as PriceList | null;
    },
  });

  const save = useMutation({
    mutationFn: async () => {
      // 1. Producto (crear o editar).
      const prod = await catalog.upsertProduct({
        id: init?.id, sku: f.sku, name: f.name, baseUnitId: f.baseUnitId, itbmsRate: f.itbmsRate,
      });
      // En edición no recreamos presentación/precio (se gestionan aparte).
      if (editing) return prod;

      // 2. Presentación base (factor 1) vía RPC del contrato.
      const pu = await catalog.upsertUnit(prod.id, f.baseUnitId, 1);
      const productUnitId = pu.id;

      // 3. Precio en la lista default (si se indicó).
      if (f.price && defaultList.data) {
        await catalog.setPrice(defaultList.data.id, productUnitId, parseFloat(f.price));
      }
      // 4. Código de barras de fábrica (si se pistoleó/tecleó).
      if (f.barcode.trim()) {
        await catalog.linkBarcode(productUnitId, f.barcode.trim(), "factory");
      }
      return prod;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["catalog"] }); onDone(); },
    onError: (e) => setMsg(e instanceof ApiError ? e.message : (e as Error).message),
  });

  const canSave = f.sku.trim() && f.name.trim() && f.baseUnitId && !save.isPending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <h2 className="mb-4 text-lg font-bold">{editing ? "Editar producto" : "Nuevo producto"}</h2>
        {msg && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{msg}</div>}

        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU" v={f.sku} on={set("sku")} placeholder="CEM-PAN-425" />
          <div>
            <label className="mb-1 block text-sm font-medium">ITBMS</label>
            <select value={f.itbmsRate} onChange={set("itbmsRate")} className="w-full rounded-lg border px-3 py-2">
              <option value="rate_7">7%</option><option value="rate_10">10%</option>
              <option value="rate_15">15%</option><option value="rate_0">0%</option>
              <option value="exempt">Exento</option>
            </select>
          </div>
        </div>
        <Field label="Nombre" v={f.name} on={set("name")} placeholder="Cemento Panamá 42.5 kg" />
        <div>
          <label className="mb-1 block text-sm font-medium">Unidad base</label>
          <select value={f.baseUnitId} onChange={set("baseUnitId")} className="mb-3 w-full rounded-lg border px-3 py-2">
            <option value="">— elegir —</option>
            {(units.data ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        {!editing && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Precio (lista minorista)" v={f.price} on={set("price")} type="number" placeholder="9.85" />
            <Field label="Código de barras (opcional)" v={f.barcode} on={set("barcode")} placeholder="pistolea o teclea" />
          </div>
        )}

        <div className="mt-5 flex gap-2">
          <button onClick={onCancel} className="btn btn-ghost flex-1">Cancelar</button>
          <button onClick={() => save.mutate()} disabled={!canSave} className="btn btn-primary flex-1 disabled:opacity-40">
            {save.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, v, on, type = "text", placeholder }: {
  label: string; v: string; on: (e: React.ChangeEvent<HTMLInputElement>) => void; type?: string; placeholder?: string;
}) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-medium">{label}</label>
      <input type={type} value={v} onChange={on} placeholder={placeholder}
        className="w-full rounded-lg border px-3 py-2 outline-none focus:border-brand" />
    </div>
  );
}
